import { C } from "../tokens";

const delay = ms => new Promise(r => setTimeout(r, ms));

// Six product categories Le-Paz Advisory Africa advises on. Each carries card bullets, a detail
// intro, and its sub-products. Copy is validated against the Kenyan market
// (IRA, CMA, and RBA kept distinct; SHA/SHIF framed as the state scheme private
// cover tops up). Slugs drive the /plans/:slug routes; keep sitemap.xml in sync.
export const CATEGORIES = [
  {
    slug: "savings-investments",
    image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=900&q=70&auto=format&fit=crop",
    name: "Savings & Investments",
    tagline: "Grow your money toward a goal you can name.",
    accent: C.gold,
    bullets: ["Endowment savings plans", "Education plans for school fees", "Investment-linked plans", "Money market funds"],
    long: "There are two honest routes to growing money in Kenya. One runs through a life insurer, with endowment, education, and investment-linked plans that pair steady growth with life cover. The other is a money market fund or unit trust, which keeps your money liquid and earning. We help you weigh guaranteed returns against flexibility, then pick the mix that fits the goal you are saving for.",
    plans: [
      { name: "Savings plans", desc: "Endowment policies that pair life cover with a guaranteed lump sum paid at maturity." },
      { name: "Education plans", desc: "A dedicated fund for school fees that keeps paying even if a parent passes away." },
      { name: "Investment plans", desc: "Investment-linked policies and money market funds that grow with the market while staying within reach." },
    ],
  },
  {
    slug: "retirement",
    image: "https://images.unsplash.com/photo-1575467678950-0c09aad418af?w=900&q=70&auto=format&fit=crop",
    name: "Retirement Plans",
    tagline: "Build your own pension, at your own pace.",
    accent: C.goldDeep,
    bullets: ["Individual Pension Plan", "Tax relief on contributions", "Annuities for life", "Income drawdown"],
    long: "You do not need an employer to save for retirement. An Individual Pension Plan lets you contribute at your own pace, with tax relief on contributions up to KES 30,000 a month, and every plan is registered with the Retirement Benefits Authority. When you retire, you can turn your savings into an annuity that pays an income for life, or an income drawdown fund that pays you while the balance stays invested.",
    plans: [
      { name: "Individual Pension Plan", desc: "A personal pension for the self-employed and anyone without an employer scheme, with tax-deductible contributions." },
      { name: "Annuities", desc: "Convert your retirement savings into a guaranteed income that lasts for the rest of your life." },
      { name: "Income drawdown", desc: "Draw a regular income in retirement while the rest of your fund stays invested and working." },
    ],
  },
  {
    slug: "life-insurance",
    image: "https://images.unsplash.com/photo-1577896849786-738ed6c78bd3?w=900&q=70&auto=format&fit=crop",
    name: "Life Insurance",
    tagline: "Provide for the people who depend on you.",
    accent: C.crimson,
    bullets: ["Term and whole life", "Last expense cover", "Critical illness benefit", "Estate planning"],
    long: "Life cover ranges from affordable term policies that protect your family for a set number of years to whole life plans that last a lifetime and build value you can pass on. It also includes last expense cover that meets funeral costs quickly, a critical illness benefit that pays a lump sum on diagnosis, and cover that hands your dependents cash at the very moment an estate is tied up in probate.",
    plans: [
      { name: "Term and whole life", desc: "Cover for a set period, or for life with a savings value your family keeps." },
      { name: "Last expense cover", desc: "Fast funeral cover for individuals, families, and groups, so a family can grieve with dignity." },
      { name: "Critical illness", desc: "A lump sum on diagnosis of conditions like cancer, stroke, or kidney failure, as a rider or a standalone policy." },
      { name: "Estate planning and wealth protection", desc: "Life policies, beneficiary nominations, and trusts that pass on what you built without family friction." },
    ],
  },
  {
    slug: "health-insurance",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&q=70&auto=format&fit=crop",
    name: "Health Insurance",
    tagline: "Get good care fast, without the bill deciding for you.",
    accent: C.rich,
    waPhone: "254795847472",
    bullets: ["Inpatient and surgery", "Outpatient and medicine", "Maternity and newborn", "Chronic and specialist care"],
    long: "Private medical cover is built around inpatient care, with outpatient, maternity, dental, optical, and chronic-condition benefits added to suit your budget. Since October 2024 it sits alongside the state scheme, the Social Health Insurance Fund run by the Social Health Authority, which replaced NHIF. Many families keep a private plan on top of SHA for higher limits, shorter waits, and a wider choice of hospitals.",
    plans: [
      { name: "Inpatient cover", desc: "Admission, surgery, ICU, and specialist fees, the core of any medical plan." },
      { name: "Outpatient cover", desc: "Consultations, tests, lab work, and prescribed medicine without being admitted." },
      { name: "Maternity and family", desc: "Antenatal care, delivery, and newborn cover, with dental and optical options." },
      { name: "Chronic and specialist care", desc: "Support for long-term conditions such as cancer and diabetes, on top of SHA and SHIF." },
    ],
  },
  {
    slug: "motor-insurance",
    image: "https://images.unsplash.com/photo-1617469767053-d3b523a0b982?w=900&q=70&auto=format&fit=crop",
    name: "Motor Insurance",
    tagline: "Cover your car and everyone else on the road.",
    accent: C.light,
    waPhone: "254795847472",
    bullets: ["Third Party Only", "Third Party, Fire and Theft", "Comprehensive cover", "Excess protector and add-ons"],
    long: "Motor cover in Kenya comes in three levels. Third Party Only meets the legal minimum by paying for injury or damage you cause to others. Third Party, Fire and Theft adds protection if your car is stolen or burnt. Comprehensive covers accidental damage to your own car as well. From there you can tailor the policy with extras such as an excess protector, windscreen cover, and a courtesy car.",
    plans: [
      { name: "Third Party Only", desc: "The minimum cover the law requires, for injury or damage you cause to other people." },
      { name: "Third Party, Fire and Theft", desc: "Third-party cover plus protection if your vehicle is stolen or damaged by fire." },
      { name: "Comprehensive", desc: "Full cover that also pays for accidental damage to your own car, with add-ons like a courtesy car." },
    ],
  },
  {
    slug: "business-property",
    image: "https://images.pexels.com/photos/30688596/pexels-photo-30688596.jpeg?auto=compress&cs=tinysrgb&w=900",
    name: "Business & Property Cover",
    tagline: "Keep your business running when things go wrong.",
    accent: C.deep,
    waPhone: "254795847472",
    bullets: ["Fire and special perils", "Burglary and theft", "Property and all-risks", "WIBA and public liability"],
    long: "Every business carries risk it should not shoulder alone. Fire and special perils cover protects your premises and stock, burglary cover responds when property is stolen, and public liability protects you if a customer is hurt on your premises. Kenyan law also requires you to cover staff for work injuries through WIBA, and you can add business interruption, fidelity guarantee, and professional indemnity to match how your business actually runs.",
    plans: [
      { name: "Fire and special perils", desc: "Cover for your building, stock, and equipment against fire and related perils." },
      { name: "Burglary and theft", desc: "Protection when business property is broken into or stolen." },
      { name: "Property and all-risks", desc: "Broader cover for premises and movable assets against a range of everyday risks." },
      { name: "WIBA and liability", desc: "Work-injury cover for staff, which the law requires of employers, plus public liability and professional indemnity." },
    ],
  },
];

// Old service slugs redirect to their new category so shared links keep working.
export const SLUG_REDIRECTS = {
  "life-insurance": "life-insurance",
  "health-insurance": "health-insurance",
  "critical-illness": "life-insurance",
  "last-expense": "life-insurance",
  "wealth-retirement": "retirement",
  "business-insurance": "business-property",
  "education-plans": "savings-investments",
  "estate-legacy": "life-insurance",
  "savings-investment": "savings-investments",
  "property-insurance": "business-property",
};

// Options for the contact form's "what can we help with" select.
export const PLAN_OPTIONS = CATEGORIES.map(c => c.name);

export function getCategory(slug) {
  return CATEGORIES.find(c => c.slug === slug) || null;
}

export async function fetchPlans() {
  await delay(400);
  return CATEGORIES;
}
